import React, { useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import AppTable from "../../common/app-table";
import AppChip from "../../common/app-chip";
import typography from "../../cssUtils/typography";
import { ColorsConstants } from "../../cssUtils/constants";

// Static mock data for the Campaign Analytics report UI.
const campaignsData = [
  {
    id: "diwali-offer",
    campaign: "Diwali offer",
    sent: 18600,
    replies: 4890,
    replyRate: 26.3,
    dominantIntent: "Pricing / interest",
    broadcasts: 3,
    buttonListReplies: 3180,
    freeTextReplies: 1710,
    intentBreakdown: [
      { intent: "Pricing / interest", value: 940, percent: 55.0, color: "#2a78d6" },
      { intent: "General query", value: 495, percent: 28.9, color: "#1baf7a" },
      { intent: "Complaint", value: 165, percent: 9.6, color: "#eda100" },
      { intent: "Opt-out request", value: 60, percent: 3.5, color: "#e34948" },
      { intent: "Unclassified", value: 50, percent: 2.9, color: "#9CA3AF" },
    ],
    broadcastDetails: [
      { name: "Diwali – launch creative", date: "Oct 12", sent: 6200, replies: 1860, replyRate: 30.0 },
      { name: "Diwali – discount reminder", date: "Oct 19", sent: 6400, replies: 1610, replyRate: 25.2 },
      { name: "Diwali – last chance", date: "Oct 26", sent: 6000, replies: 1420, replyRate: 23.7 },
    ],
  },
  {
    id: "sip-reminder-weekly",
    campaign: "SIP reminder, weekly",
    sent: 9400,
    replies: 940,
    replyRate: 10.0,
    dominantIntent: "General query",
    broadcasts: 1,
    buttonListReplies: 620,
    freeTextReplies: 320,
    intentBreakdown: [
      { intent: "General query", value: 190, percent: 59.4, color: "#2a78d6" },
      { intent: "Pricing / interest", value: 78, percent: 24.4, color: "#1baf7a" },
      { intent: "Complaint", value: 32, percent: 10.0, color: "#eda100" },
      { intent: "Opt-out request", value: 12, percent: 3.7, color: "#e34948" },
      { intent: "Unclassified", value: 8, percent: 2.5, color: "#9CA3AF" },
    ],
    broadcastDetails: [],
  },
  {
    id: "price-hike-notice",
    campaign: "Price hike notice",
    sent: 3354,
    replies: 140,
    replyRate: 4.2,
    dominantIntent: "Complaint",
    broadcasts: 1,
    buttonListReplies: 35,
    freeTextReplies: 105,
    intentBreakdown: [
      { intent: "Complaint", value: 58, percent: 55.2, color: "#eda100" },
      { intent: "Pricing / interest", value: 22, percent: 21.0, color: "#2a78d6" },
      { intent: "Opt-out request", value: 15, percent: 14.3, color: "#e34948" },
      { intent: "General query", value: 7, percent: 6.7, color: "#1baf7a" },
      { intent: "Unclassified", value: 3, percent: 2.8, color: "#9CA3AF" },
    ],
    broadcastDetails: [],
  },
];

const intentChipColors = {
  "Pricing / interest": { bg: "#EAF2FD", text: "#2a78d6" },
  "General query": { bg: "#E7F7F0", text: "#0E8F5E" },
  "Complaint": { bg: "#FDECEC", text: "#C22B2B" },
  "Opt-out request": { bg: "#FDECEC", text: "#C22B2B" },
  "Unclassified": { bg: "#F1F2F4", text: "#61748C" },
};

const ReplyRateCell = ({ value }) => (
  <Typography
    sx={{
      ...typography.bodyLarge,
      fontWeight: 600,
      color: value < 10 ? "#C22B2B" : "#1D2330",
    }}
  >
    {value.toFixed(1)}%
  </Typography>
);

const IntentChip = ({ intent }) => {
  const colors = intentChipColors[intent] || { bg: "#F1F2F4", text: "#61748C" };
  return (
    <AppChip
      label={intent}
      sx={{ backgroundColor: colors.bg, color: colors.text, fontWeight: 600 }}
    />
  );
};

const CampaignAnalyticsTable = ({ campaigns, selectedCampaignId, onSelectCampaign }) => {
  const columns = [
    {
      id: "campaign",
      label: "Campaign",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: "#1D2330" }}>
          {row.campaign}
        </Typography>
      ),
    },
    {
      id: "sent",
      label: "Sent",
      align: "right",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {row.sent.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: "replies",
      label: "Replies",
      align: "right",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {row.replies.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: "replyRate",
      label: "Reply rate",
      align: "right",
      render: (row) => <ReplyRateCell value={row.replyRate} />,
    },
    {
      id: "dominantIntent",
      label: "Dominant intent",
      render: (row) => <IntentChip intent={row.dominantIntent} />,
    },
    {
      id: "broadcasts",
      label: "Broadcasts",
      align: "right",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryPurple100 }}>
          {row.broadcasts}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <Typography sx={{ ...typography.bodySmall, color: "#61748C", mb: 1 }}>
        Campaigns, this period
      </Typography>

      <AppTable
        columns={columns}
        rows={campaigns}
        rowKey={(row) => row.id}
        emptyMessage="No campaigns found"
        autoAdjustHeight={false}
        maxHeight={520}
        onRowClick={(row) => onSelectCampaign(row)}
        rowSx={(row) =>
          row.id === selectedCampaignId
            ? { backgroundColor: "#EAF2FD !important", "&:hover": { backgroundColor: "#EAF2FD !important" } }
            : {}
        }
        isRowExpandable={(row) => row.broadcasts >= 2 && row.broadcastDetails?.length > 0}
        renderExpanded={(row) => (
          <Box sx={{ px: 3, py: 1.5, backgroundColor: "#FAFBFC" }}>
            {row.broadcastDetails.map((b) => (
              <Box
                key={b.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  py: 0.75,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  sx={{
                    ...typography.bodySmall,
                    fontStyle: "italic",
                    color: ColorsConstants.primaryPurple100,
                    minWidth: 220,
                  }}
                >
                  {b.name}
                </Typography>
                <Typography sx={{ ...typography.bodySmall, color: "#61748C", minWidth: 60 }}>
                  {b.date}
                </Typography>
                <Typography sx={{ ...typography.bodySmall, color: "#61748C", ml: "auto" }}>
                  {b.sent.toLocaleString()} sent&nbsp;&nbsp;&nbsp;{b.replies.toLocaleString()} replies&nbsp;&nbsp;&nbsp;
                  <Box component="span" sx={{ fontWeight: 600, color: "#1D2330" }}>
                    {b.replyRate.toFixed(1)}% reply rate
                  </Box>
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      />

      <Typography sx={{ ...typography.bodySmall, fontStyle: "italic", color: "#9CA3AF" }}>
        Expand chevron appears only for campaigns with 2+ broadcasts. Single-broadcast campaigns render flat.
      </Typography>
    </Box>
  );
};

const StatItem = ({ label, value }) => (
  <Box sx={{ flex: 1, minWidth: 140 }}>
    <Typography sx={{ ...typography.bodySmall, color: "#61748C", mb: 0.5 }}>{label}</Typography>
    <Typography sx={{ ...typography.h1, fontWeight: 700, color: "#1D2330" }}>{value}</Typography>
  </Box>
);

const IntentBar = ({ intent, value, percent, color }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.75 }}>
      <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>{intent}</Typography>
      <Typography sx={{ ...typography.bodySmall, color: "#61748C" }}>
        {value.toLocaleString()} ({percent.toFixed(1)}%)
      </Typography>
    </Box>
    <Box sx={{ position: "relative", width: "100%", height: 6 }}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: 0,
          width: "100%",
          height: 2,
          transform: "translateY(-50%)",
          borderRadius: 1,
          backgroundColor: "#E5E7EB",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: 0,
          width: `${percent}%`,
          height: 6,
          transform: "translateY(-50%)",
          borderRadius: 3,
          backgroundColor: color,
        }}
      />
    </Box>
  </Box>
);

const CampaignDetailPanel = ({ campaign }) => {
  if (!campaign) return null;

  return (
    <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #E5E7EB" }}>
      <Typography sx={{ ...typography.bodySmall, color: "#61748C", mb: 0.5 }}>
        Selected campaign
      </Typography>
      <Typography sx={{ ...typography.h1, fontWeight: 700, color: ColorsConstants.primaryBlue100, mb: 3 }}>
        {campaign.campaign}
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 5, mb: 4 }}>
        <StatItem label="Sent" value={campaign.sent.toLocaleString()} />
        <StatItem label="Replies" value={campaign.replies.toLocaleString()} />
        <StatItem label="Button / list replies" value={campaign.buttonListReplies.toLocaleString()} />
        <StatItem label="Free-text replies" value={campaign.freeTextReplies.toLocaleString()} />
      </Box>

      <Typography sx={{ ...typography.h2, fontWeight: 600, color: "#1D2330", mb: 2 }}>
        Intent breakdown — free-text replies
      </Typography>

      <Box sx={{ width: "100%" }}>
        {campaign.intentBreakdown.map((item) => (
          <IntentBar
            key={item.intent}
            intent={item.intent}
            value={item.value}
            percent={item.percent}
            color={item.color}
          />
        ))}
      </Box>
    </Box>
  );
};

// Static UI preview for the Campaign Analytics report. Data is hardcoded to
// match the design reference -- no API wiring yet.
const CampaignAnalyticsReport = () => {
  const [selectedCampaignId, setSelectedCampaignId] = useState(campaignsData[0]?.id);

  const selectedCampaign = campaignsData.find((c) => c.id === selectedCampaignId);

  return (
    <Box sx={{ mt: 3, mb: 1, mx: 3 }}>
      <Typography
        sx={{
          ...typography.bodySmall,
          color: ColorsConstants.primaryPurple100,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          mb: 0.5,
        }}
      >
        Report 2
      </Typography>
      <Typography sx={{ ...typography.h1, fontSize: "24px", fontWeight: 700, color: "#1D2330", mb: 2.5 }}>
        Campaign Analytics (Dummy Data- In Development)
      </Typography>

      <Paper sx={{ p: 3, border: "1px solid #E5E7EB", borderRadius: "10px", boxShadow: "none" }}>
        <CampaignAnalyticsTable
          campaigns={campaignsData}
          selectedCampaignId={selectedCampaignId}
          onSelectCampaign={(row) => setSelectedCampaignId(row.id)}
        />
        <CampaignDetailPanel campaign={selectedCampaign} />
      </Paper>
    </Box>
  );
};

export default CampaignAnalyticsReport;
